import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, productsApi } from '@/lib/api-client';

export function useAdminTenants(params: Record<string, string | number | boolean | undefined> = {}) {
  return useQuery({
    queryKey: ['admin', 'tenants', params],
    queryFn: () => api.get<{ data: any[]; pagination: any }>('/admin/tenants', params),
  });
}

export function useAdminTenantDetail(id: string) {
  return useQuery({
    queryKey: ['admin', 'tenants', id],
    queryFn: () => api.get<{ data: any }>(`/admin/tenants/${id}`),
    enabled: !!id,
  });
}

export function useActivateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put(`/admin/tenants/${id}/activate`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

export function useDeactivateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put(`/admin/tenants/${id}/deactivate`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

export function useToggleTenantModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      moduleName,
      isActive,
      locationId,
    }: {
      tenantId: string;
      moduleName: string;
      isActive: boolean;
      locationId?: string;
    }) => api.put(`/admin/tenants/${tenantId}/modules/${moduleName}/toggle`, { isActive, locationId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      // The sidebar reads ['modules', locationId] to decide which nav entries
      // to show. Without this the super admin's own navigation keeps the
      // pre-toggle state until a reload — visible now that a module gate can
      // add or remove an entry outright rather than just grey it out.
      await queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
  });
}

export function useUpdateTenantFeatureFlags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      canAddLocations,
      useGenericProductImages,
      allowTenantProductImageUpload,
      allowCategoryDepartmentImageUpload,
      variableBarcodeEnabled,
      barcodeAutogenEnabled,
      productExportEnabled,
    }: {
      tenantId: string;
      canAddLocations?: boolean;
      useGenericProductImages?: boolean;
      allowTenantProductImageUpload?: boolean;
      allowCategoryDepartmentImageUpload?: boolean;
      variableBarcodeEnabled?: boolean;
      barcodeAutogenEnabled?: boolean;
      productExportEnabled?: boolean;
    }) =>
      api.put(`/admin/tenants/${tenantId}/feature-flags`, {
        canAddLocations,
        useGenericProductImages,
        allowTenantProductImageUpload,
        allowCategoryDepartmentImageUpload,
        variableBarcodeEnabled,
        barcodeAutogenEnabled,
        productExportEnabled,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

export function useActivateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, locationId, isActive }: { tenantId: string; locationId: string; isActive: boolean }) =>
      api.put(`/admin/tenants/${tenantId}/locations/${locationId}/activate`, { isActive }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

/** Super-admin update of a location's basic config: name, IANA timezone,
 *  and/or the Receive Payments payment method switches (Card / ACH). */
export function useUpdateAdminLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      locationId,
      timezone,
      name,
      rpCardEnabled,
      rpAchEnabled,
      type,
      email,
    }: {
      tenantId: string;
      locationId: string;
      timezone?: string;
      name?: string;
      rpCardEnabled?: boolean;
      rpAchEnabled?: boolean;
      type?: 'company' | 'warehouse' | 'private_warehouse';
      /** Contact email vendors reach the location at (PO Reply-To); '' clears it. */
      email?: string | null;
    }) =>
      api.put(`/admin/tenants/${tenantId}/locations/${locationId}`, {
        ...(email !== undefined ? { email } : {}),
        ...(type !== undefined ? { type } : {}),
        ...(timezone !== undefined ? { timezone } : {}),
        ...(name !== undefined ? { name } : {}),
        ...(rpCardEnabled !== undefined ? { rpCardEnabled } : {}),
        ...(rpAchEnabled !== undefined ? { rpAchEnabled } : {}),
      }),
    onSuccess: async (_res, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', vars.tenantId] });
    },
  });
}

// ── Global Settings (Lookup Table) ──────────────────────────────────────────

export interface GlobalSetting {
  key: string;
  value: string | null;
  environment: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useGlobalSettings() {
  return useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.get<{ data: GlobalSetting[] }>('/admin/settings'),
  });
}

export function useUpsertGlobalSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      value,
      environment,
      description,
    }: {
      key: string;
      value?: string | null;
      environment?: string;
      description?: string | null;
    }) => api.put(`/admin/settings/${encodeURIComponent(key)}`, { value, environment, description }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });
}

export function useCreateGlobalSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      value,
      environment,
      description,
    }: {
      key: string;
      value?: string | null;
      environment?: string;
      description?: string | null;
    }) => api.post('/admin/settings', { key, value, environment, description }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });
}

export function useDeleteGlobalSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.delete(`/admin/settings/${encodeURIComponent(key)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });
}

// ── Super-admin: wipe a location's per-location product data ──────────────────
// Removes only the location-specific layer (pricing, units, assignment/inventory,
// per-location field-override locks) so the location can be re-imported. Base
// products/departments/categories/vendors are tenant-wide and untouched. The
// X-Tenant-Id header (productsApi's 3rd arg) lets the super admin target the
// tenant being viewed in the admin page.

// Sent as `batchSize`, NOT `limit`: a `{"limit":<n>}` body is read by the prod
// WAF's SQLi rule as a SQL `LIMIT <n>` clause and 403'd at the edge before
// reaching the products service. `batchSize` passes cleanly.
const WIPE_BATCH_SIZE = 1000;

/** How many products still carry per-location data at this location. */
export function useLocationPerLocationCount(tenantId: string, locationId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'per-location-count', tenantId, locationId],
    queryFn: () =>
      productsApi.get<{ total: number }>(`/products/locations/${locationId}/per-location-count`, undefined, tenantId),
    enabled: enabled && !!tenantId && !!locationId,
  });
}

/** Runs the client-driven chunk loop: repeatedly calls /wipe-batch until the
 *  location has no per-location product data left, reporting progress. */
export function useWipeLocationProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      locationId,
      onProgress,
    }: {
      tenantId: string;
      locationId: string;
      onProgress?: (done: number, total: number) => void;
    }) => {
      const { total } = await productsApi.get<{ total: number }>(
        `/products/locations/${locationId}/per-location-count`,
        undefined,
        tenantId,
      );
      let cleared = 0;
      onProgress?.(0, total);

      // Loop until the server reports nothing left. `remaining` is recomputed
      // server-side each batch, so this terminates even if rows are added
      // concurrently far slower than we delete.
      for (;;) {
        const res = await productsApi.post<{ deletedProducts: number; remaining: number }>(
          `/products/locations/${locationId}/wipe-batch`,
          { batchSize: WIPE_BATCH_SIZE },
          tenantId,
        );
        cleared += res.deletedProducts;
        onProgress?.(Math.min(cleared, total), total);
        if (res.deletedProducts === 0 || res.remaining === 0) break;
      }
      return { cleared, total };
    },
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'per-location-count', vars.tenantId, vars.locationId],
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}
